import React from 'react';
import './FmListPrimitives.css';

const cx = (...parts: Array<string | false | null | undefined>): string =>
  parts.filter(Boolean).join(' ');

export interface FmMetaLineItem {
  id: string;
  icon?: React.ReactNode;
  label: React.ReactNode;
}

export interface FmMetaLineProps {
  items: FmMetaLineItem[];
  /**
   * @internal DS-internal className prop; forbidden in route-owned code.
   */
  className?: string;
  'data-testid'?: string;
}

export type FmCellStackAlign = 'start' | 'end';

export interface FmCellStackProps {
  primary?: React.ReactNode;
  secondary?: React.ReactNode;
  meta?: React.ReactNode;
  metaItems?: FmMetaLineItem[];
  footer?: React.ReactNode;
  align?: FmCellStackAlign;
  /**
   * @internal DS-internal className prop; forbidden in route-owned code.
   */
  className?: string;
  'data-testid'?: string;
  children?: React.ReactNode;
}

export interface FmActionGroupProps {
  children: React.ReactNode;
  density?: 'compact' | 'comfortable';
  align?: 'start' | 'end';
  wrap?: boolean;
  collapseOnMobile?: boolean;
  /**
   * @internal DS-internal className prop; forbidden in route-owned code.
   */
  className?: string;
  'data-testid'?: string;
}

export const FmMetaLine: React.FC<FmMetaLineProps> = ({
  items,
  className,
  'data-testid': dataTestId,
}) => (
  <span className={cx('fm-meta-line', className)} data-testid={dataTestId}>
    {items.map((item) => (
      <span key={item.id} className="fm-meta-line__item">
        {item.icon ? <span className="fm-meta-line__icon">{item.icon}</span> : null}
        <span>{item.label}</span>
      </span>
    ))}
  </span>
);

export const FmCellStack: React.FC<FmCellStackProps> = ({
  primary,
  secondary,
  meta,
  metaItems,
  footer,
  align = 'start',
  className,
  'data-testid': dataTestId,
  children,
}) => (
  <span
    className={cx(
      'fm-cell-stack',
      align === 'end' && 'fm-cell-stack--end',
      className,
    )}
    data-testid={dataTestId}
  >
    {primary ? <span className="fm-cell-stack__primary">{primary}</span> : null}
    {secondary ? <span className="fm-cell-stack__secondary">{secondary}</span> : null}
    {metaItems && metaItems.length > 0 ? <FmMetaLine items={metaItems} /> : meta}
    {footer ? <span className="fm-cell-stack__footer">{footer}</span> : null}
    {children}
  </span>
);

export const FmActionGroup: React.FC<FmActionGroupProps> = ({
  children,
  density = 'comfortable',
  align = 'start',
  wrap = true,
  collapseOnMobile = false,
  className,
  'data-testid': dataTestId,
}) => (
  <div
    className={cx(
      'fm-action-group',
      density === 'compact' && 'fm-action-group--compact',
      align === 'end' && 'fm-action-group--end',
      !wrap && 'fm-action-group--nowrap',
      collapseOnMobile && 'fm-action-group--collapse-mobile',
      className,
    )}
    data-testid={dataTestId}
  >
    {children}
  </div>
);
